// User Types
export interface User {
  id: string;
  email: string;
  name: string; // Full name from Azure AD
  role: UserRole;
  isActive?: boolean;
  azureId?: string;
  picture?: string;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  USER = 'USER',
  VIEWER = 'VIEWER',
  ADMIN = 'ADMIN',
  SUPERADMIN = 'SUPERADMIN'
}

export interface AuthResponse {
  user: User;
  token: string;
}

// LoginRequest entfernt - nur Azure AD SSO wird verwendet

// Item Types
export interface Item {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  barcode?: string;
  qrCode?: string;
  status: ItemStatus;
  quantity: number;
  minQuantity: number;
  isRental: boolean;
  serialNumber?: string;
  isManageInSAP: boolean;
  sapPlantNumbers: string[];
  attributes?: Record<string, any>;
  locationId?: string;
  categoryId?: string;
  location?: Location;
  category?: Category;
  createdBy: User;
  updatedBy?: User;
  createdAt: string;
  updatedAt: string;
}

export enum ItemStatus {
  AVAILABLE = 'AVAILABLE',
  CHECKED_OUT = 'CHECKED_OUT',
  MAINTENANCE = 'MAINTENANCE',
  RETIRED = 'RETIRED'
}

export interface CreateItemRequest {
  name: string;
  description?: string;
  sku?: string;
  barcode?: string;
  qrCode?: string;
  status?: ItemStatus;
  quantity: number;
  minQuantity?: number;
  locationId?: string;
  categoryId?: string;
  attributes?: Record<string, any>;
  isRental?: boolean;
  serialNumber?: string;
  isManageInSAP?: boolean;
  sapPlantNumbers?: string[];
}

export interface UpdateItemRequest {
  name?: string;
  description?: string;
  sku?: string;
  barcode?: string;
  qrCode?: string;
  status?: ItemStatus;
  quantity?: number;
  minQuantity?: number;
  locationId?: string;
  categoryId?: string;
  attributes?: Record<string, any>;
  isRental?: boolean;
  serialNumber?: string;
  isManageInSAP?: boolean;
  sapPlantNumbers?: string[];
}

// Location Types
export interface Location {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  plantNumber?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  parent?: Location;
  children?: Location[];
}

export interface CreateLocationRequest {
  name: string;
  description?: string;
  parentId?: string;
  plantNumber?: string;
}

export interface UpdateLocationRequest {
  name?: string;
  description?: string;
  parentId?: string;
  plantNumber?: string;
  isActive?: boolean;
}

// Category Types
export interface Category {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  isActive: boolean;
  parent?: Category;
  children?: Category[];
  createdAt: string;
  updatedAt: string;
  _count?: {
    items: number;
    children: number;
  };
}

export interface CreateCategoryRequest {
  name: string;
  description?: string;
  parentId?: string;
}

export interface UpdateCategoryRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
}

// Transaction Types
export interface Transaction {
  id: string;
  type: TransactionType;
  quantity: number;
  notes?: string;
  status?: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  sapPlantNumber?: string;
  sapStatus: SapStatus;
  sapTransferredAt?: string;
  sapTransferredBy?: string;
  item: {
    id: string;
    name: string;
    sku?: string;
    isManageInSAP?: boolean;
  };
  user?: {
    id: string;
    name: string;
  };
  sapTransferrer?: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export enum TransactionType {
  CHECKOUT = 'CHECKOUT',
  CHECKIN = 'CHECKIN',
  CANCEL = 'CANCEL',
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE'
}

// Warenverkehr Types
export interface DirectCheckoutRequest {
  itemId: string;
  quantity: number;
  targetUserId?: string;
  targetLocationId?: string;
  notes?: string;
}

export interface RestockRequest {
  itemId: string;
  quantity: number;
  notes?: string;
}

export interface CreateTransactionRequest {
  itemId: string;
  userId?: string; // Optional - for admin transactions without specific user
  type: TransactionType | 'CHECKOUT' | 'CHECKIN' | 'RESTOCK' | 'MAINTENANCE' | 'RETIRE';
  quantity: number;
  notes?: string;
  dueDate?: Date;
  locationId?: string;
  sapPlantNumber?: string; // SAP Werknummer für SAP-geführte Items
}

export enum SapStatus {
  NOT_TRANSFERRED = 'NOT_TRANSFERRED',
  TRANSFERRED = 'TRANSFERRED'
}

// System Settings Types
export interface SystemSetting {
  id: string;
  key: string;
  value: string;
  description?: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSystemSettingRequest {
  key: string;
  value: string;
  description?: string;
  isPublic?: boolean;
}

export interface UpdateSystemSettingRequest {
  value?: string;
  description?: string;
  isPublic?: boolean;
}

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Search and Filter Types
export interface SearchParams {
  search?: string;
  status?: ItemStatus;
  locationId?: string;
  categoryId?: string;
  userId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// QR Code Types
export interface QRCodeData {
  itemId: string;
  itemName: string;
  sku?: string;
  barcode?: string;
}

// PDF Label Types
export interface LabelData {
  itemName: string;
  sku?: string;
  barcode?: string;
  qrCode?: string;
  location?: string;
  attributes?: Record<string, any>;
}

export interface LabelConfig {
  width: number;
  height: number;
  fontSize: number;
  lineHeight: number;
  margin: number;
}

// Form Types
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'select' | 'textarea' | 'checkbox' | 'date';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  validation?: any;
}

// UI Types
export interface TableColumn<T> {
  key: keyof T;
  header: string;
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  width?: string;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

// Theme Types
export type Theme = 'light' | 'dark' | 'system';

// Navigation Types
export interface NavigationItem {
  title: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: string;
  children?: NavigationItem[];
  roles?: UserRole[];
}

// Dashboard Types
export interface DashboardStats {
  totalItems: number;
  availableItems: number;
  checkedOutItems: number;
  lowStockItems: number;
  totalUsers: number;
  recentTransactions: Transaction[];
}

// Error Types
export interface AppError {
  message: string;
  statusCode?: number;
  details?: Record<string, any>;
}
